import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SelectUtilityDialog } from '~/components/select-utility-dialog';
import { LDClient, LDFlags } from '~/providers';
import { QueryParamsContext } from '~/providers/query-params';
import { Dashboard } from '~/screens/dashboard';
import { UtilityAccountSelection } from '~/screens/utility-account-selection';
import { createFunnelPacket, fetchExistingUtilityAccounts } from '~/services';
import { UtilityAccount } from '~/services/fetch-existing-utility-accounts';
import { INTERNAL_ROUTES } from '~/utils/constants';
import { hardRedirect } from '~/utils/for-warbler';
import { store } from '~/utils/store';
import { sendToSegment } from '~/utils/tracking';

const trackStarted = context => {
  sendToSegment({
    type: 'utility_account_selection_step_started',
    userId: context.userId,
  });
};
export const getExistingUtilityAccounts = async context => {
  const { utilityAccounts } = await fetchExistingUtilityAccounts({
    userId: context.userId,
  });
  return { utilityAccounts };
};

export const trackUtilityAccountChosen = context => {
  sendToSegment({
    type: 'utility_account_selection_chosen',
    selectedUtilityAccountId: context.selectedUtilityAccountId!,
    userId: context.userId,
  });
};

const trackFinishedCreation = ({ userId, newUtilityId, utilityAccountId }) => {
  sendToSegment({
    type: 'utility_account_creation_step_completed',
    userId,
    utilityId: newUtilityId,
    utilityAccountId,
  });
};

export const trackStartedCreation = async context => {
  sendToSegment({
    type: 'utility_account_creation_step_started',
    userId: context.userId,
  });
};

const hasCompletedSolarSignUp = ({ utilityAccounts, selectedUtilityAccountId }) => {
  const utilityAccount = utilityAccounts.find(ua => ua.id === selectedUtilityAccountId);
  return !!utilityAccount?.hasVnmProduct;
};
export const WelcomeBackWrapper = () => {
  const navigate = useNavigate();
  const [utilityAccounts, setUtilityAccounts] = useState<UtilityAccount[]>([]);
  const [isUserSelectedNewUtility, setIsUserSelectedNewUtility] = useState(false);
  const [isUserHasCompletedSolarSignUp, setisUserHasCompletedSolarSignUp] = useState(false);
  const [selectNewUtilityError, setSelectNewUtilityError] = useState<string | undefined>(undefined);
  const { resumingUser } = useContext(QueryParamsContext);

  const userId = store.get('USER_ID');
  const handleOnConnectNewUtility = () => {
    trackStartedCreation({ userId });
    setIsUserSelectedNewUtility(true);
  };

  const handleUtilityAccountSelection = async payload => {
    store.set('RESUMING_USER_UTILITY_ACCOUNT_ID', payload.selectedUtilityAccountId);
    store.set('RESUMING_IN_PROGRESS', false);
    trackUtilityAccountChosen({
      selectedUtilityAccountId: payload.selectedUtilityAccountId,
      userId,
    });

    if (
      hasCompletedSolarSignUp({
        utilityAccounts,
        selectedUtilityAccountId: payload.selectedUtilityAccountId,
      })
    ) {
      setisUserHasCompletedSolarSignUp(true);
    } else {
      hardRedirect(INTERNAL_ROUTES.SPONSORED_SOLAR);
    }
  };

  const createUtilityAccount = async ({ accountId, utilityId }) => {
    const { utilityAccount } = await createFunnelPacket({
      accountId,
      utilityId,
    });
    return { utilityAccountId: utilityAccount.id };
  };

  useEffect(() => {
    if (!userId) {
      navigate(INTERNAL_ROUTES.EXPIRED_SESSION, {
        state: { url: INTERNAL_ROUTES.SPONSORED_SOLAR },
      });
    }
    trackStarted({ userId });
    const fetchData = async () => {
      try {
        const { utilityAccounts } = await getExistingUtilityAccounts({
          userId,
        });
        setUtilityAccounts(utilityAccounts);
      } catch (error) {
        navigate(INTERNAL_ROUTES.EXPIRED_SESSION, {
          state: { url: INTERNAL_ROUTES.SPONSORED_SOLAR },
        });
      }
    };
    if (utilityAccounts.length === 0) {
      fetchData();
    }
  }, [userId]);

  const getDashboardScreen = () => {
    return (
      <Dashboard
        context={{ userId: userId! }}
        onBack={() => {
          setisUserHasCompletedSolarSignUp(false);
        }}
      />
    );
  };
  const getUtilityAccountSelectionScreen = () => {
    return (
      <>
        <UtilityAccountSelection
          context={{ utilityAccounts }}
          onConnectNewUtility={handleOnConnectNewUtility}
          flags={{
            showNewUtilityButton: LDClient.variation(LDFlags.createNewUtilityAccount),
          }}
          next={handleUtilityAccountSelection}
        />
        {isUserSelectedNewUtility && (
          <SelectUtilityDialog
            error={selectNewUtilityError}
            onClose={() => {
              setIsUserSelectedNewUtility(false);
            }}
            onSubmit={async data => {
              try {
                const { utilityAccountId } = await createUtilityAccount({
                  accountId: resumingUser!.packet!.account!.id,
                  utilityId: data.utilityId,
                });
                trackFinishedCreation({
                  userId,
                  newUtilityId: data.utilityId,
                  utilityAccountId,
                });
                store.set('RESUMING_USER_UTILITY_ACCOUNT_ID', utilityAccountId);
              } catch (error) {
                setSelectNewUtilityError(error.message);
                return;
              }

              setIsUserSelectedNewUtility(false);
            }}
            pending={false}
          />
        )}
      </>
    );
  };

  const getScreens = () => {
    if (isUserHasCompletedSolarSignUp) {
      return getDashboardScreen();
    }
    return getUtilityAccountSelectionScreen();
  };
  return utilityAccounts.length > 0 ? getScreens() : null;
};
